import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Check, AlertCircle, FileText, Lock, Pen } from "lucide-react";
import { IconContract, IconSignature, IconShield } from "../components/MinimalIcons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractSignaturePage() {
  const { contractId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [step, setStep] = useState(1); // 1: Review, 2: Sign, 3: Confirm
  const [agreed, setAgreed] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  useEffect(() => {
    if (step === 2 && canvasRef.current) {
      initCanvas();
    }
  }, [step]);

  const fetchContract = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContract(data);
      } else {
        toast.error("Contrato não encontrado");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Erro ao carregar contrato");
    } finally {
      setLoading(false);
    }
  };

  const initCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = 200;
    
    // Set drawing style
    ctx.strokeStyle = '#171717';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearSignature = () => {
    setSignatureData(null);
    if (canvasRef.current) {
      initCanvas();
    }
  };

  const submitSignature = async () => {
    if (!agreed || !signatureData) {
      toast.error("Aceite os termos e assine o contrato");
      return;
    }

    setSigning(true);
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/sign-digital`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          signature_image: signatureData,
          agreed_terms: agreed,
          device_info: {
            user_agent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success("Contrato assinado com sucesso!");
        setStep(3);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erro ao assinar contrato");
      }
    } catch (error) {
      toast.error("Erro ao processar assinatura");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  const isClientSigning = user?.user_id === contract?.client_id;
  const alreadySigned = isClientSigning ? contract?.client_signed : contract?.provider_signed;
  const otherPartySigned = isClientSigning ? contract?.provider_signed : contract?.client_signed;

  return (
    <div className="min-h-screen bg-white" data-testid="contract-signature-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" strokeWidth={1.5} />
          </button>
          <div>
            <p className="font-medium text-neutral-900 text-sm">Assinatura Digital</p>
            <p className="text-neutral-400 text-xs">Contrato #{contractId?.slice(-6)}</p>
          </div>
        </div>
      </header>

      <main className="px-5 py-8 max-w-2xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-400'
              }`}>
                {step > s ? <Check className="w-4 h-4" strokeWidth={2} /> : s}
              </div>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-neutral-900' : 'bg-neutral-100'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Review Contract */}
        {step === 1 && (
          <div className="space-y-6 animate-up">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <IconContract className="w-8 h-8 text-neutral-700" />
              </div>
              <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Revise o Contrato</h1>
              <p className="text-neutral-500 mt-2">Leia atentamente antes de assinar</p>
            </div>

            {/* Contract Summary */}
            <div className="p-6 bg-neutral-50 rounded-2xl space-y-4">
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Valor Total</span>
                <span className="font-semibold text-neutral-900">R$ {contract?.total_value?.toLocaleString("pt-BR")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Sinal</span>
                <span className="font-medium text-neutral-900">R$ {contract?.signal_amount?.toLocaleString("pt-BR")}</span>
              </div>
              {contract?.retention_percentage > 0 && (
                <div className="flex justify-between">
                  <span className="text-neutral-500 text-sm">Retenção</span>
                  <span className="font-medium text-neutral-900">{(contract.retention_percentage * 100).toFixed(0)}% por {contract.retention_days} dias</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Etapas</span>
                <span className="font-medium text-neutral-900">{contract?.milestones?.length || 0} milestones</span>
              </div>
            </div>

            {/* Signature Status */}
            <div className="space-y-3">
              <div className={`flex items-center gap-3 p-4 rounded-xl ${contract?.client_signed ? 'bg-emerald-50' : 'bg-neutral-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${contract?.client_signed ? 'bg-emerald-100' : 'bg-neutral-100'}`}>
                  {contract?.client_signed ? <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} /> : <Pen className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />}
                </div>
                <div>
                  <p className="font-medium text-neutral-900 text-sm">Cliente</p>
                  <p className="text-neutral-500 text-xs">{contract?.client_signed ? 'Assinado' : 'Pendente'}</p>
                </div>
              </div>
              <div className={`flex items-center gap-3 p-4 rounded-xl ${contract?.provider_signed ? 'bg-emerald-50' : 'bg-neutral-50'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${contract?.provider_signed ? 'bg-emerald-100' : 'bg-neutral-100'}`}>
                  {contract?.provider_signed ? <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} /> : <Pen className="w-4 h-4 text-neutral-400" strokeWidth={1.5} />}
                </div>
                <div>
                  <p className="font-medium text-neutral-900 text-sm">Prestador</p>
                  <p className="text-neutral-500 text-xs">{contract?.provider_signed ? 'Assinado' : 'Pendente'}</p>
                </div>
              </div>
            </div>

            {alreadySigned ? (
              <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" strokeWidth={2} />
                  <p className="text-emerald-800 font-medium text-sm">Você já assinou este contrato</p>
                </div>
              </div>
            ) : (
              <Button 
                className="w-full btn-minimal btn-primary h-12"
                onClick={() => setStep(2)}
              >
                Continuar para Assinatura
              </Button>
            )}
          </div>
        )}

        {/* Step 2: Sign */}
        {step === 2 && (
          <div className="space-y-6 animate-up">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-neutral-100 flex items-center justify-center mx-auto mb-4">
                <IconSignature className="w-8 h-8 text-neutral-700" />
              </div>
              <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Assine o Contrato</h1>
              <p className="text-neutral-500 mt-2">Desenhe sua assinatura abaixo</p>
            </div>

            {/* Signature Canvas */}
            <div className="space-y-3">
              <div className="border border-neutral-200 rounded-2xl overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full touch-none cursor-crosshair"
                  data-testid="signature-canvas"
                />
              </div>
              <div className="flex justify-between">
                <p className="text-neutral-400 text-xs">Assine com o dedo ou mouse</p>
                <button 
                  onClick={clearSignature}
                  className="text-neutral-500 text-xs hover:text-neutral-900"
                >
                  Limpar
                </button>
              </div>
            </div>

            {/* Agreement Checkbox */}
            <label className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
                data-testid="agree-checkbox"
              />
              <span className="text-sm text-neutral-700">
                Li e concordo com todos os termos do contrato. Entendo que esta assinatura tem validade jurídica.
              </span>
            </label>

            {/* Security Info */}
            <div className="flex items-start gap-3 p-4 bg-neutral-100 rounded-xl">
              <IconShield className="w-5 h-5 text-neutral-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900">Assinatura Segura</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Sua assinatura será registrada com hash SHA-256, timestamp, IP e identificação do dispositivo.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1 h-12"
                onClick={() => setStep(1)}
              >
                Voltar
              </Button>
              <Button 
                className="flex-1 btn-minimal btn-primary h-12"
                onClick={submitSignature}
                disabled={!agreed || !signatureData || signing}
              >
                {signing ? "Assinando..." : "Confirmar Assinatura"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmed */}
        {step === 3 && (
          <div className="space-y-6 animate-up text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-emerald-600" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-neutral-900 tracking-tight">Contrato Assinado!</h1>
              <p className="text-neutral-500 mt-2">Sua assinatura foi registrada com sucesso</p>
            </div>

            <div className="p-6 bg-neutral-50 rounded-2xl text-left space-y-3">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
                <span className="text-sm text-neutral-700">Hash SHA-256 gerado e armazenado</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-neutral-500" strokeWidth={1.5} />
                <span className="text-sm text-neutral-700">Logs de auditoria registrados</span>
              </div>
              <div className="flex items-center gap-2">
                <IconShield className="w-4 h-4 text-neutral-500" />
                <span className="text-sm text-neutral-700">Contrato imutável e verificável</span>
              </div>
            </div>

            {!otherPartySigned && (
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <p className="text-sm text-amber-800 text-left">
                    Aguardando assinatura da outra parte. O pagamento só será habilitado após ambas as assinaturas.
                  </p>
                </div>
              </div>
            )}

            <Button 
              className="w-full btn-minimal btn-primary h-12"
              onClick={() => navigate(`/contracts/${contractId}`)}
            >
              Ver Contrato
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
